import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Input,
  List,
  Tag,
  Avatar,
  Tooltip,
  Divider,
} from "antd";
import {
  useList,
  useDelete,
  useNavigation,
} from "@refinedev/core";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { ContentType } from "@/types";

const { Title, Text } = Typography;

export const TypeList: React.FC = () => {
  const [selectedType, setSelectedType] = useState<ContentType | null>(null);
  const { push } = useNavigation();
  const { mutate: deleteType } = useDelete();

  const {
    data: typesData,
    isLoading,
    refetch,
  } = useList<ContentType>({
    resource: "types",
    pagination: { mode: "off" },
  });

  const handleDelete = (id: number) => {
    deleteType(
      {
        resource: "types",
        id,
        successNotification: () => ({
          message: "Type supprimé",
          description: "Le type de contenu a été supprimé avec succès",
          type: "success",
        }),
      },
      {
        onSuccess: () => {
          setSelectedType(null);
          refetch();
        },
      }
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
          Types de Contenu
        </Title>
        <Text type="secondary">
          Gérez les types de contenu de votre plateforme (Film, Série, Documentaire, etc.)
        </Text>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => push("/types/create")}
          >
            Ajouter un type
          </Button>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card
            title="Types de contenu"
            extra={
              <Tag color="blue">{typesData?.data?.length || 0} types</Tag>
            }
          >
            <Input
              placeholder="Rechercher un type..."
              prefix={<SearchOutlined />}
              style={{ marginBottom: 16 }}
            />

            <List
              dataSource={typesData?.data || []}
              loading={isLoading}
              renderItem={(type) => (
                <List.Item
                  key={type.id}
                  style={{
                    cursor: "pointer",
                    backgroundColor:
                      selectedType?.id === type.id ? "#f0f8ff" : "transparent",
                    padding: "12px 16px",
                    borderRadius: 8,
                    marginBottom: 8,
                    border:
                      selectedType?.id === type.id
                        ? "1px solid #1890ff"
                        : "1px solid #f0f0f0",
                  }}
                  onClick={() => setSelectedType(type)}
                  actions={[
                    <Tooltip title="Modifier" key="edit">
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          push(`/types/edit/${type.id}`);
                        }}
                      />
                    </Tooltip>,
                    <Tooltip title="Supprimer" key="delete">
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(type.id);
                        }}
                      />
                    </Tooltip>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={<AppstoreOutlined />}
                        style={{
                          backgroundColor:
                            selectedType?.id === type.id ? "#1890ff" : "#f0f0f0",
                          color:
                            selectedType?.id === type.id ? "white" : "#666",
                        }}
                      />
                    }
                    title={<Text strong>{type.title}</Text>}
                    description={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        ID: {type.id}
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Détails du type">
            {selectedType ? (
              <div>
                <div style={{ marginBottom: 24 }}>
                  <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
                    {selectedType.title}
                  </Title>
                  <Tag color="blue">ID: {selectedType.id}</Tag>
                </div>

                <Divider />

                <Space>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => push(`/types/edit/${selectedType.id}`)}
                  >
                    Modifier
                  </Button>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(selectedType.id)}
                  >
                    Supprimer
                  </Button>
                </Space>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <AppstoreOutlined
                  style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }}
                />
                <div>
                  <Text type="secondary">
                    Sélectionnez un type pour voir les détails
                  </Text>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TypeList;
